import { createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";
import { VolumeApi } from "@/lib/volume-api";
import { Onboarding } from "@/routes/(app)/_main-layout/_dashboard/-components/onboarding";
import { useQuery } from "@tanstack/react-query";
import { CurrencyIcons } from "@/components/svg/currencyIcons";
import { RevenueCard } from "@/routes/(app)/_main-layout/_dashboard/-components/revenue-card";
import { Revenue } from "@/types/revenue";
import { useState } from "react";
import { RevenueGraph } from "@/routes/(app)/_main-layout/_dashboard/-components/revenue-graph";
import { AuthorityRequired } from "@/lib/auth";
import { useMerchant } from "@/hooks/use-merchant";
import { useAnalytics } from "@/lib/analytics";

// Tooltip text constants for revenue cards
const TOOLTIP_TEXT_LAST_24H =
  "The last 24 hours of transactions relative to the time of the page visit";
const TOOLTIP_TEXT_LAST_7_DAYS =
  "Last 7 complete days of transactions, ie if today is Saturday at 3pm, the last 7 days will be from Friday at 00:00 to Saturday at 23:59";
const TOOLTIP_TEXT_LAST_30_DAYS =
  "Last 30 days of transactions, ie if today is the 3rd of January, the last 30 days will be from the 4th December 00:00 to the 2nd of January 23:59";

export const Route = createFileRoute("/(app)/_main-layout/_dashboard/")({
  component: HomeComponent,
});

function HomeComponent() {
  const context = Route.useRouteContext();
  const applicationId = context.selectedApplicationId;

  const thisYear = dayjs().year();

  const startDateTimeUtc = dayjs(`${thisYear}-01-01`).format(
    "YYYY-MM-DDT00:00:00",
  );
  const endDateTimeUtc = dayjs(`${thisYear + 1}-01-01`).format(
    "YYYY-MM-DDT00:00:00",
  );
  const startDateTimeUtcLastYear = dayjs(`${thisYear - 1}-01-01`).format(
    "YYYY-MM-DDT00:00:00",
  );
  const endDateTimeUtcLastYear = dayjs(`${thisYear}-01-01`).format(
    "YYYY-MM-DDT00:00:00",
  );

  return (
    <div className="w-full">
      <Onboarding
        accessToken={context.accessToken}
        applicationId={applicationId}
        merchantId={context.merchantId}
      />
      <AuthorityRequired
        authoritiesRequired={["VIEW_ONLY"]}
        authAuthorities={context.authorities}
      >
        <SummarySection
          currency="GBP"
          applicationId={applicationId}
          endDateTimeUtc={endDateTimeUtc}
          startDateTimeUtc={startDateTimeUtc}
          endDateTimeUtcLastYear={endDateTimeUtcLastYear}
          startDateTimeUtcLastYear={startDateTimeUtcLastYear}
        />
        <SummarySection
          currency="EUR"
          applicationId={applicationId}
          endDateTimeUtc={endDateTimeUtc}
          startDateTimeUtc={startDateTimeUtc}
          endDateTimeUtcLastYear={endDateTimeUtcLastYear}
          startDateTimeUtcLastYear={startDateTimeUtcLastYear}
        />
      </AuthorityRequired>
      {/* <div className="relative overflow-x-auto shadow-md sm:rounded-2xl"></div> */}
    </div>
  );
}

function Last24Hours({
  applicationId,
  currency,
}: {
  applicationId: string;
  currency: keyof Revenue;
}) {
  const context = Route.useRouteContext();
  const api = new VolumeApi(context.accessToken, applicationId);

  const startDateTimeUtcLast24Hours = dayjs()
    .subtract(1, "day")
    .format("YYYY-MM-DDThh:mm:ss");

  const revenueLast24HoursQuery = useQuery({
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryKey: ["revenue-last-24-hours", applicationId],
    queryFn: () =>
      api.getPaymentsSummary({
        startDateTimeUtc: startDateTimeUtcLast24Hours,
        endDateTimeUtc: dayjs().add(1, "day").format("YYYY-MM-DDT23:59:59"),
        paymentStatuses: "COMPLETED,SETTLED",
        sort: "createdAt,desc",
        merchantId: context.merchantId,
      }),
  });

  return (
    <RevenueCard
      period={"Last 24h"}
      value={revenueLast24HoursQuery.data?.[currency]?.totalAmount}
      pageVisitTime={revenueLast24HoursQuery.dataUpdatedAt}
      tooltipText={TOOLTIP_TEXT_LAST_24H}
      currency={currency}
      isLoading={revenueLast24HoursQuery.isPending}
    />
  );
}

function Last7Days({
  applicationId,
  currency,
}: {
  applicationId: string;
  currency: keyof Revenue;
}) {
  const context = Route.useRouteContext();
  const api = new VolumeApi(context.accessToken, applicationId);

  const startDateTimeUtcLast7FullDays = dayjs()
    .subtract(7, "day")
    .format("YYYY-MM-DDT00:00:00");
  const endDateTimeUtcLast7FullDays = dayjs()
    .subtract(1, "day")
    .format("YYYY-MM-DDT23:59:59");

  const revenueLast7FullDaysQuery = useQuery({
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryKey: ["revenue-last-7-full-days", applicationId],
    queryFn: () =>
      api.getPaymentsSummary({
        startDateTimeUtc: startDateTimeUtcLast7FullDays,
        endDateTimeUtc: endDateTimeUtcLast7FullDays,
        paymentStatuses: "COMPLETED,SETTLED",
        sort: "createdAt,desc",
        merchantId: context.merchantId,
      }),
  });

  return (
    <RevenueCard
      period={"Last 7 days"}
      value={revenueLast7FullDaysQuery.data?.[currency]?.totalAmount}
      pageVisitTime={revenueLast7FullDaysQuery.dataUpdatedAt}
      tooltipText={TOOLTIP_TEXT_LAST_7_DAYS}
      currency={currency}
      isLoading={revenueLast7FullDaysQuery.isPending}
    />
  );
}

function Last30Days({
  applicationId,
  currency,
}: {
  applicationId: string;
  currency: keyof Revenue;
}) {
  const context = Route.useRouteContext();
  const api = new VolumeApi(context.accessToken, applicationId);

  const startDateTimeUtcLast30Days = dayjs()
    .subtract(30, "day")
    .format("YYYY-MM-DDT00:00:00");
  const endDateTimeUtcLast30Days = dayjs()
    .subtract(1, "day")
    .format("YYYY-MM-DDT23:59:59");

  const revenueQuery = useQuery({
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryKey: ["revenue-last-30-days", applicationId],
    queryFn: () =>
      api.getPaymentsSummary({
        startDateTimeUtc: startDateTimeUtcLast30Days,
        endDateTimeUtc: endDateTimeUtcLast30Days,
        paymentStatuses: "COMPLETED,SETTLED",
        sort: "createdAt,desc",
        merchantId: context.merchantId,
      }),
  });

  return (
    <RevenueCard
      period={"Last 30 days"}
      value={revenueQuery.data?.[currency]?.totalAmount}
      pageVisitTime={revenueQuery.dataUpdatedAt}
      tooltipText={TOOLTIP_TEXT_LAST_30_DAYS}
      currency={currency}
      isLoading={revenueQuery.isPending}
    />
  );
}

function SummarySection({
  currency,
  applicationId,
  endDateTimeUtc,
  endDateTimeUtcLastYear,
  startDateTimeUtc,
  startDateTimeUtcLastYear,
}: {
  currency: "GBP" | "EUR";
  applicationId: string;
  startDateTimeUtc: string;
  endDateTimeUtc: string;
  startDateTimeUtcLastYear: string;
  endDateTimeUtcLastYear: string;
}) {
  const [periodType, setPeriodType] = useState<"YEAR" | "MONTH">("MONTH");
  const context = Route.useRouteContext();
  const merchant = useMerchant();
  const analytics = useAnalytics();

  const api = new VolumeApi(context.accessToken, applicationId);

  const onPeriodTypeChange = (periodType: "YEAR" | "MONTH") => {
    setPeriodType(periodType);
    analytics.track("dashboard_period_changed", {
      period: periodType,
    });
  };

  // Calculate date ranges based on period type
  const getDateRanges = () => {
    if (periodType === "MONTH") {
      // For month view: this month vs last month
      const thisMonthStart = dayjs()
        .startOf("month")
        .format("YYYY-MM-DDT00:00:00");
      const thisMonthEnd = dayjs().endOf("month").format("YYYY-MM-DDT23:59:59");
      const lastMonthStart = dayjs()
        .subtract(1, "month")
        .startOf("month")
        .format("YYYY-MM-DDT00:00:00");
      const lastMonthEnd = dayjs()
        .subtract(1, "month")
        .endOf("month")
        .format("YYYY-MM-DDT23:59:59");

      return {
        thisPeriodStart: thisMonthStart,
        thisPeriodEnd: thisMonthEnd,
        lastPeriodStart: lastMonthStart,
        lastPeriodEnd: lastMonthEnd,
      };
    } else {
      // For year view: this year vs last year
      return {
        thisPeriodStart: startDateTimeUtc,
        thisPeriodEnd: endDateTimeUtc,
        lastPeriodStart: startDateTimeUtcLastYear,
        lastPeriodEnd: endDateTimeUtcLastYear,
      };
    }
  };

  const dateRanges = getDateRanges();

  const revenueQuery = useQuery({
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryKey: ["revenue", applicationId, periodType],
    queryFn: () =>
      api.getRevenue({
        startDateTimeUtc: dateRanges.thisPeriodStart,
        endDateTimeUtc: dateRanges.thisPeriodEnd,
        paymentStatuses: "COMPLETED,SETTLED",
        sort: "createdAt,desc",
        periodType: periodType === "MONTH" ? "DAY" : "MONTH",
        merchantId: context.merchantId,
      }),
  });
  const revenueLastYearQuery = useQuery({
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryKey: ["revenueLastYear", applicationId, periodType],
    queryFn: () =>
      api.getRevenue({
        startDateTimeUtc: dateRanges.lastPeriodStart,
        endDateTimeUtc: dateRanges.lastPeriodEnd,
        paymentStatuses: "COMPLETED,SETTLED",
        sort: "createdAt,desc",
        periodType: periodType === "MONTH" ? "DAY" : "MONTH",
        merchantId: context.merchantId,
      }),
  });
  /**
   * Only show relevant currency if user has this currency for selected application
   *
   * Find application by applicationId, if it has the currency from props then show it
   */
  if (
    !merchant.applications
      ?.find((x) => x.applicationId === applicationId)
      ?.payeeDetailsPerCurrency?.some((x) => x.forCurrency === currency)
  ) {
    return null;
  }

  /**
   * Empty state - only show when there's no data at all, not when graph is loading
   */
  if (
    !revenueQuery.data ||
    !revenueLastYearQuery.data ||
    (!revenueQuery.data[currency] && !revenueLastYearQuery.data[currency])
  ) {
    return (
      <>
        <h4 className="flex items-center gap-2 my-2">
          <div className="bg-white rounded-full p-1.5">
            {CurrencyIcons[currency]}
          </div>
          {currency} Summary
        </h4>
        <div className="flex">
          <div className="lg:flex gap-4">
            <Last24Hours applicationId={applicationId} currency={currency} />
            <Last7Days applicationId={applicationId} currency={currency} />
            <Last30Days applicationId={applicationId} currency={currency} />
          </div>
        </div>
        <RevenueGraph
          revenueThisYear={undefined}
          revenueLastYear={undefined}
          isLoading={revenueQuery.isPending || revenueLastYearQuery.isPending}
          periodType={periodType}
          onPeriodTypeChange={onPeriodTypeChange}
        />
      </>
    );
  }
  /**
   * If data
   */
  return (
    <>
      <h4 className="flex items-center gap-2 my-2">
        <div className="bg-white rounded-full p-1.5">
          {CurrencyIcons[currency]}
        </div>
        {currency} Summary
      </h4>
      <div className="flex">
        <div className="lg:flex gap-4">
          <Last24Hours applicationId={applicationId} currency={currency} />
          <Last7Days applicationId={applicationId} currency={currency} />
          <Last30Days applicationId={applicationId} currency={currency} />
        </div>
      </div>
      <RevenueGraph
        revenueThisYear={revenueQuery.data?.[currency] ?? undefined}
        revenueLastYear={revenueLastYearQuery.data?.[currency] ?? undefined}
        isLoading={revenueQuery.isPending || revenueLastYearQuery.isPending}
        periodType={periodType}
        onPeriodTypeChange={onPeriodTypeChange}
      />
    </>
  );
}
